// src/lib/resources/topics/registry.ts

/**
 * @fileoverview Single source of truth for resource topic hub taxonomy.
 * @description Maps knowledge domains to glossary terms, tools, templates,
 * guides, and blog posts. Every curated slug is validated in unit tests.
 * @module resources/topics/registry
 */

import type { ResourceTopicDefinition, ResourceTopicId } from '~/lib/resources/topics/types'

/**
 * Ordered registry of resource topic hubs. Order reflects roadmap priority:
 * creative performance measurement first, then platform and benchmark clusters.
 */
export const RESOURCE_TOPICS: readonly ResourceTopicDefinition[] = [
  {
    id: 'creative-analytics',
    slug: 'creative-analytics',
    title: 'Creative Analytics',
    navLabel: 'Creative Analytics',
    tagline: 'Measure, benchmark, and interpret creative performance metrics.',
    description:
      'Master data-driven creative optimization with definitions, calculators, and benchmark tools for the metrics that predict ad performance — from thumbstop and hold rate through ThruPlay and video completion.',
    iconName: 'bar-chart-box',
    keywords: [
      'creative analytics',
      'creative performance metrics',
      'video ad metrics',
      'thumbstop rate',
      'hold rate',
      'thruplay rate',
      'creative measurement',
    ],
    relatedTopicSlugs: ['video-performance', 'marketing-benchmarks', 'creative-testing'],
    content: {
      glossaryTerms: [
        { category: 'metrics', slug: 'thumbstop-rate-tsr' },
        { category: 'metrics', slug: 'hold-rate' },
        { category: 'metrics', slug: 'thruplay-rate' },
        { category: 'metrics', slug: 'video-completion-rate-vcr' },
        { category: 'metrics', slug: 'thumbstop-click-rate' },
        { category: 'metrics', slug: 'cost-per-thruplay' },
      ],
      toolSlugs: [
        'hook-rate-benchmark',
        'thruplay-rate-benchmark',
        'creative-quality-grader',
        'video-dropoff-calculator',
        // The CPV calculator computes cost per ThruPlay — the interactive tool
        // for this cluster's `cost-per-thruplay` glossary term.
        'cpv-calculator',
      ],
      templateSlugs: ['ab-test-tracker-template'],
      guideSlugs: ['video-ad-hook-optimization-guide', 'thruplay-rate-interpretation-guide', 'video-creative-metrics-benchmarks-2026-guide'],
      blogPostSlugs: [
        'data-storytelling-transform-marketing-metrics-into-creative-insights',
        'creative-feature-models-beyond-the-winning-ad',
        'how-to-read-video-metrics-in-meta-ads-manager',
      ],
      blogTopicSlugs: ['creative-analytics'],
      comparisonSlugs: ['ctr-vs-thumbstop-rate', 'hold-rate-vs-thruplay-rate', 'vcr-vs-thruplay-rate'],
    },
  },
  {
    id: 'creative-testing',
    slug: 'creative-testing',
    title: 'Creative Testing',
    navLabel: 'Creative Testing',
    tagline: 'Frameworks, sample-size tools, and methodology for rigorous ad tests.',
    description:
      'Design, run, and interpret creative tests with statistical confidence. Explore A/B testing definitions, sample-size calculators, tracking templates, and methodology guides for Meta, TikTok, and YouTube creative.',
    iconName: 'test-tube',
    keywords: [
      'creative testing',
      'ad creative testing',
      'creative testing framework',
      'a/b testing ads',
      'creative testing methodology',
      'statistical significance',
    ],
    relatedTopicSlugs: ['creative-analytics', 'creative-strategy', 'attribution-measurement', 'experimentation'],
    content: {
      glossaryTerms: [
        { category: 'creative', slug: 'creative-testing' },
        { category: 'creative', slug: 'ab-testing' },
        { category: 'metrics', slug: 'statistical-significance' },
        { category: 'metrics', slug: 'sample-size' },
        { category: 'metrics', slug: 'confidence-interval' },
        { category: 'creative', slug: 'ad-variations' },
      ],
      toolSlugs: [
        'creative-testing-calculator',
        'ugc-cost-calculator',
        'ab-test-statistical-significance-calculator',
        'creative-quality-grader',
      ],
      templateSlugs: ['ab-test-tracker-template'],
      guideSlugs: ['creative-testing-framework-guide'],
      blogPostSlugs: [
        'how-to-run-a-meta-creative-test-that-actually-proves-something',
        'agentic-video-ads-claude-code-ads-framework',
      ],
      blogTopicSlugs: ['creative-testing'],
    },
    overview: [
      'Creative testing is where opinion ends and evidence starts — but only when the test is designed to produce evidence. The most common failure mode is not "bad creative"; it is calling a winner after 200 conversions when the plan required 2,000, or comparing variants on different audiences and attributing the gap to the hook.',
      'A rigorous creative test fixes three things up front: the metric that maps to the business decision (CTR and hold rate for learning; CPA or ROAS for scaling), the minimum detectable effect you care about, and the sample size each variant needs to detect it at 95% confidence. The calculators in this hub translate those inputs into budget and duration — use them before launch, not after you are already emotionally invested in a leader.',
      'On Meta and TikTok, platform delivery adds noise: dynamic budget shift, learning phase, and audience expansion all change who sees which variant. Keep audiences and placements stable for the test window, limit the number of variants so each earns enough impressions, and resist peeking. When a variant wins with statistical separation, tag what made it win — hook style, format, offer framing — so the next iteration is a refinement, not a random relaunch.',
    ],
    faqs: [
      {
        question: 'How many ad variants should I test at once?',
        answer:
          'Enough that each variant can reach your pre-set sample target — typically 3–5 distinct concepts, not 15 near-duplicates. More variants on a fixed budget starve them all of data and inflate false-positive risk. If budget is tight, run sequential tests rather than parallel ones with underpowered cells.',
      },
      {
        question: 'What metric should I use to pick a creative winner?',
        answer:
          'Match the metric to the decision. Upper-funnel learning tests (new audiences, new hooks) should judge on thumbstop rate, hold rate, or CTR. Scaling decisions need CPA, ROAS, or incremental lift from a holdout. Picking a "winner" on CTR when you optimize for purchases is how teams scale ads that stop at the click.',
      },
      {
        question: 'When is a creative test conclusive?',
        answer:
          'When the gap between variants exceeds your margin of error at the sample size you planned — not when one variant leads after a weekend. Use the significance calculator in this hub; if confidence intervals overlap, the test is inconclusive regardless of how the dashboard looks.',
      },
      {
        question: 'What are creative testing best practices for A/B ad experiments?',
        answer:
          'Decide the metric that maps to the business decision before launch (CTR or hold rate for learning tests; CPA, ROAS, or holdout lift for scaling decisions), and pre-compute the sample size each variant needs at 95% confidence. Test 3–5 distinct concepts rather than 15 near-duplicates, keep audiences and placements stable for the test window, and resist peeking before variants reach the planned sample. When a winner separates statistically, tag what made it win — hook style, format, offer framing — so the next round is a refinement, not a random relaunch.',
      },
    ],
  },
  {
    id: 'creative-strategy',
    slug: 'creative-strategy',
    title: 'Creative Strategy',
    navLabel: 'Creative Strategy',
    tagline: 'Plan, diversify, and refresh creative before performance decays.',
    description:
      'Build creative systems that scale — from persona development and content calendars to fatigue detection and diversification frameworks that keep campaigns converting.',
    iconName: 'lightbulb',
    keywords: [
      'creative strategy',
      'creative fatigue',
      'creative diversification',
      'ad creative strategy',
      'buyer persona marketing',
    ],
    relatedTopicSlugs: ['creative-testing', 'meta-advertising', 'creative-analytics', 'social-proof-creative'],
    content: {
      glossaryTerms: [
        { category: 'creative', slug: 'creative-fatigue' },
        { category: 'creative', slug: 'ad-variations' },
      ],
      toolSlugs: [
        'creative-quality-grader',
        'ad-mockup-generator',
        'notification-generator',
        'app-store-screenshot-generator',
        'screenshot-beautifier',
        'buyer-persona-interactive-generator',
        'color-palette-generator',
        'svg-animation-generator',
        'ad-safe-zone-checker',
        'fake-chat-generator',
        'ai-chat-mockup-generator',
        'email-thread-mockup-generator',
        'review-testimonial-generator',
        'thruplay-rate-benchmark',
        'video-dropoff-calculator',
      ],
      templateSlugs: [
        'digital-marketing-buyer-persona-template',
        'content-calendar-template',
      ],
      guideSlugs: ['how-to-spot-a-fake-screenshot', 'thruplay-rate-interpretation-guide'],
      blogPostSlugs: [
        'creative-fatigue-in-meta-ads-detection-and-management-strategies',
        'creative-diversification-how-to-create-high-converting-targeted-ad-creative',
        'meta-andromeda-ads-operational-playbook',
      ],
      blogTopicSlugs: ['creative-strategy'],
    },
  },
  {
    id: 'meta-advertising',
    slug: 'meta-advertising',
    title: 'Meta Advertising',
    navLabel: 'Meta Ads',
    tagline: 'Metrics, mockups, and benchmarks for Facebook and Instagram campaigns.',
    description:
      'Optimize Meta ad performance with platform-specific metric definitions, CPM and ROAS calculators, ad mockup generators, and creative fatigue guidance for Reels, Feed, and Stories placements.',
    iconName: 'megaphone',
    keywords: [
      'meta ads',
      'facebook ads',
      'instagram ads',
      'meta advertising metrics',
      'meta reels ads',
      'meta ad benchmarks',
    ],
    relatedTopicSlugs: ['video-performance', 'marketing-benchmarks', 'creative-strategy', 'social-proof-creative'],
    content: {
      glossaryTerms: [
        { category: 'platform', slug: 'meta-reels-ads' },
        { category: 'metrics', slug: 'click-through-rate-ctr' },
        { category: 'metrics', slug: 'cost-per-mille-cpm' },
        { category: 'creative', slug: 'creative-fatigue' },
      ],
      toolSlugs: [
        'hook-rate-benchmark',
        'paid-social-benchmarks',
        'cpm-calculator',
        'cpa-calculator',
        'roas-calculator',
        'ad-mockup-generator',
        'ad-safe-zone-checker',
        'notification-generator',
        'fake-chat-generator',
        'ai-chat-mockup-generator',
        'email-thread-mockup-generator',
        'review-testimonial-generator',
        'thruplay-rate-benchmark',
        'instagram-post-mockup-generator',
        'facebook-post-mockup-generator',
      ],
      templateSlugs: ['marketing-budget-template'],
      guideSlugs: [
        'meta-ad-creative-video-specs-guide',
        'how-to-spot-a-fake-screenshot',
        'thruplay-rate-interpretation-guide',
        'video-creative-metrics-benchmarks-2026-guide',
      ],
      blogPostSlugs: [
        'creative-fatigue-in-meta-ads-detection-and-management-strategies',
        'what-is-a-good-thruplay-rate-2026-benchmarks',
        'how-to-read-video-metrics-in-meta-ads-manager',
        'social-proof-mockups-email-chat-generators-without-figma',
        'meta-andromeda-ads-operational-playbook',
      ],
      // Placeholder: blog topic exists; posts auto-roll in as they publish.
      blogTopicSlugs: ['social-media-advertising'],
    },
  },
  {
    id: 'tiktok-advertising',
    slug: 'tiktok-advertising',
    title: 'TikTok Advertising',
    navLabel: 'TikTok Ads',
    tagline: 'Platform metrics, mockups, and video benchmarks for TikTok campaigns.',
    description:
      'Run better TikTok ads with Spark Ads definitions, post mockup generators, and cross-platform video benchmarks that compare hold rate and completion metrics against Meta and YouTube.',
    iconName: 'global',
    keywords: [
      'tiktok ads',
      'tiktok advertising',
      'tiktok spark ads',
      'tiktok ad metrics',
      'tiktok creative benchmarks',
    ],
    relatedTopicSlugs: ['video-performance', 'meta-advertising', 'marketing-benchmarks'],
    content: {
      glossaryTerms: [
        { category: 'platform', slug: 'tiktok-ads' },
        { category: 'platform', slug: 'tiktok-spark-ads' },
      ],
      toolSlugs: [
        'hook-rate-benchmark',
        'paid-social-benchmarks',
        'tiktok-post-mockup-generator',
        'ad-safe-zone-checker',
        'thruplay-rate-benchmark',
        'video-dropoff-calculator',
      ],
      templateSlugs: [],
      guideSlugs: [
        'tiktok-ad-creative-video-specs-guide',
        'thruplay-rate-interpretation-guide',
        'video-ad-hook-optimization-guide',
      ],
      blogPostSlugs: ['thruplay-tiktok-meta-youtube-comparison-2026'],
      blogTopicSlugs: [],
    },
  },
  {
    id: 'video-performance',
    slug: 'video-performance',
    title: 'Video Performance',
    navLabel: 'Video Performance',
    tagline: 'Hook, hold, ThruPlay, and completion metrics for video ads.',
    description:
      'Diagnose video ad performance with the full metric stack — thumbstop and hook rate through hold rate, ThruPlay, VCR, and drop-off analysis — plus benchmark tools and platform comparison guides.',
    iconName: 'video',
    keywords: [
      'video ad performance',
      'thruplay rate',
      'hold rate',
      'video completion rate',
      'thumbstop rate',
      'video dropoff',
      'vcr vs thruplay',
    ],
    relatedTopicSlugs: ['creative-analytics', 'meta-advertising', 'marketing-benchmarks'],
    content: {
      glossaryTerms: [
        { category: 'metrics', slug: 'thruplay-rate' },
        { category: 'metrics', slug: 'hold-rate' },
        { category: 'metrics', slug: 'thumbstop-rate-tsr' },
        { category: 'metrics', slug: 'video-completion-rate-vcr' },
        { category: 'metrics', slug: 'cost-per-thruplay' },
        { category: 'metrics', slug: 'impressions' },
        { category: 'metrics', slug: 'view-rate' },
      ],
      toolSlugs: [
        'hook-rate-benchmark',
        'thruplay-rate-benchmark',
        'video-dropoff-calculator',
      ],
      templateSlugs: [],
      guideSlugs: ['thruplay-rate-interpretation-guide', 'video-ad-hook-optimization-guide', 'video-creative-metrics-benchmarks-2026-guide', 'meta-ad-creative-video-specs-guide', 'tiktok-ad-creative-video-specs-guide'],
      blogPostSlugs: [
        'what-is-a-good-thruplay-rate-2026-benchmarks',
        'how-to-calculate-video-completion-rate-vcr-thruplay-vtr',
        'thruplay-tiktok-meta-youtube-comparison-2026',
        'how-to-read-video-metrics-in-meta-ads-manager',
      ],
      // Keep this hub on explicitly curated video posts. Auto-including the
      // whole `ad-performance` blog topic would also feature generic CPA /
      // unit-economics articles that have no video workflow.
      blogTopicSlugs: [],
      comparisonSlugs: ['hold-rate-vs-thruplay-rate', 'vcr-vs-thruplay-rate'],
    },
  },
  {
    id: 'attribution-measurement',
    slug: 'attribution-measurement',
    title: 'Attribution & Measurement',
    navLabel: 'Attribution',
    tagline: 'ROAS, MER, incrementality, and full-funnel efficiency metrics.',
    description:
      'Move beyond platform-reported ROAS with MER, incrementality, LTV, and budget allocation tools — plus glossary definitions for the measurement stack modern performance teams rely on.',
    iconName: 'pie-chart',
    keywords: [
      'marketing attribution',
      'roas vs mer',
      'marketing efficiency ratio',
      'incrementality testing',
      'customer ltv',
      'marketing measurement',
    ],
    relatedTopicSlugs: ['marketing-benchmarks', 'creative-testing', 'meta-advertising'],
    content: {
      glossaryTerms: [
        { category: 'metrics', slug: 'return-on-ad-spend-roas' },
        { category: 'metrics', slug: 'marketing-efficiency-ratio-mer' },
        { category: 'metrics', slug: 'attributed-marketing-efficiency-ratio-amer' },
        { category: 'metrics', slug: 'new-marketing-efficiency-ratio-nmer' },
        { category: 'metrics', slug: 'cost-per-acquisition-cpa' },
        { category: 'metrics', slug: 'customer-acquisition-cost-cac' },
        { category: 'general', slug: 'incrementality' },
        { category: 'general', slug: 'attribution-window' },
      ],
      toolSlugs: [
        'roas-calculator',
        'cpa-calculator',
        'mer-calculator',
        'marketing-incrementality-calculator',
        'customer-ltv-calculator',
        'marketing-budget-calculator',
      ],
      templateSlugs: ['marketing-budget-template'],
      guideSlugs: ['incrementality-testing-guide'],
      blogPostSlugs: [
        'statistical-noise-unmasking-the-illusion-of-insights-in-modern-marketing',
        'how-to-run-an-incrementality-test-that-changes-budget-decisions',
        'what-is-a-good-cpa-2026-benchmarks',
      ],
      blogTopicSlugs: ['data-science'],
      comparisonSlugs: ['mer-vs-roas', 'cpa-vs-cac', 'roas-vs-roi'],
    },
  },
  {
    id: 'marketing-benchmarks',
    slug: 'marketing-benchmarks',
    title: 'Marketing Benchmarks',
    navLabel: 'Benchmarks',
    tagline: 'Industry benchmark ranges for CTR, CPM, and video engagement metrics.',
    description:
      'Compare your campaign metrics against curated benchmark ranges for Meta, TikTok, and cross-platform video performance — with interactive analyzers and deep-dive benchmark articles.',
    iconName: 'line-chart',
    keywords: [
      'marketing benchmarks',
      'ad benchmarks',
      'ctr benchmark',
      'cpm benchmark',
      'thruplay benchmark',
      'thumbstop benchmark',
      'good roas benchmark',
      'cpa benchmark',
      'what is a good cpa',
    ],
    relatedTopicSlugs: ['creative-analytics', 'video-performance', 'meta-advertising'],
    content: {
      glossaryTerms: [
        { category: 'metrics', slug: 'click-through-rate-ctr' },
        { category: 'metrics', slug: 'cost-per-mille-cpm' },
        // CPC joins CTR/CPM now that the paid-social analyzer covers all
        // three cost/click metrics from this hub.
        { category: 'metrics', slug: 'cost-per-click-cpc' },
        { category: 'metrics', slug: 'thumbstop-rate-tsr' },
        { category: 'metrics', slug: 'hold-rate' },
        { category: 'metrics', slug: 'thruplay-rate' },
        { category: 'metrics', slug: 'return-on-ad-spend-roas' },
        { category: 'metrics', slug: 'cost-per-acquisition-cpa' },
      ],
      toolSlugs: [
        'paid-social-benchmarks',
        'hook-rate-benchmark',
        'thruplay-rate-benchmark',
        'creative-quality-grader',
        'cpm-calculator',
        'cpa-calculator',
        'roas-calculator',
      ],
      templateSlugs: [],
      guideSlugs: ['video-creative-metrics-benchmarks-2026-guide', 'thruplay-rate-interpretation-guide'],
      blogPostSlugs: [
        'what-is-a-good-thruplay-rate-2026-benchmarks',
        'what-is-a-good-cpa-2026-benchmarks',
        'how-to-read-video-metrics-in-meta-ads-manager',
      ],
      blogTopicSlugs: ['ad-performance'],
      comparisonSlugs: [
        'mer-vs-roas',
        'hold-rate-vs-thruplay-rate',
        'ctr-vs-thumbstop-rate',
        'cpa-vs-cac',
        'vcr-vs-thruplay-rate',
        'roas-vs-roi',
      ],
    },
  },
  {
    id: 'data-visualization',
    slug: 'data-visualization',
    title: 'Data Visualization',
    navLabel: 'Data Viz',
    tagline: 'Pick the right chart, then build it — chart-type definitions, mockup tools, and storytelling.',
    description:
      'Choose and build the right chart for your marketing data. A chart-type reference (bar, line, funnel, scatter, heatmap, and more) covering when to use each and how to read it, paired with mockup generators for charts and data tables and guidance on turning metrics into a clear data story.',
    iconName: 'dashboard',
    keywords: [
      'data visualization',
      'chart types',
      'which chart to use',
      'horizontal bar chart',
      'funnel chart',
      'marketing dashboard',
      'data storytelling',
    ],
    relatedTopicSlugs: ['creative-analytics', 'marketing-benchmarks', 'experimentation'],
    content: {
      glossaryTerms: [
        { category: 'charts', slug: 'horizontal-bar-chart' },
        { category: 'charts', slug: 'vertical-bar-chart' },
        { category: 'charts', slug: 'grouped-bar-chart' },
        { category: 'charts', slug: 'stacked-bar-chart' },
        { category: 'charts', slug: 'line-chart' },
        { category: 'charts', slug: 'area-chart' },
        { category: 'charts', slug: 'funnel-chart' },
        { category: 'charts', slug: 'pie-chart' },
        { category: 'charts', slug: 'scatter-plot' },
        { category: 'charts', slug: 'histogram' },
        { category: 'charts', slug: 'heat-map' },
        { category: 'charts', slug: 'stacked-area-chart' },
        { category: 'charts', slug: 'sparkline' },
        { category: 'charts', slug: 'box-plot' },
        { category: 'charts', slug: 'bubble-chart' },
        { category: 'charts', slug: 'chord-diagram' },
        { category: 'charts', slug: 'density-plot' },
        { category: 'charts', slug: 'donut-chart' },
        { category: 'charts', slug: 'network-graph' },
        { category: 'charts', slug: 'radar-chart' },
        { category: 'charts', slug: 'sankey-diagram' },
        { category: 'charts', slug: 'stream-graph' },
        { category: 'charts', slug: 'treemap' },
        { category: 'charts', slug: 'word-cloud' },
      ],
      toolSlugs: [
        'chart-graph-mockup-generator',
        'data-table-generator',
      ],
      templateSlugs: [],
      guideSlugs: ['marketing-data-visualization-guide'],
      blogPostSlugs: [
        'data-storytelling-transform-marketing-metrics-into-creative-insights',
        'which-chart-for-which-metric-marketing-data-visualization-guide',
      ],
      blogTopicSlugs: ['data-visualization'],
    },
    overview: [
      'Choosing a chart is a decision about the question, not the data. Before picking a type, name the relationship you want the reader to see: a comparison between categories, a trend over time, a part-to-whole breakdown, a distribution, a relationship between two variables, or a drop-off through stages. Each of those maps to a small set of chart types that encode it honestly — and a much larger set that will technically render but mislead.',
      'For most marketing reporting, four families do the heavy lifting. Bar charts (horizontal when labels are long or categories are ranked, vertical for a handful of ordered categories) win comparisons. Line and area charts own change over time — they match how we read left-to-right and make trends, seasonality, and inflection points obvious. Funnel charts express stage-to-stage conversion and where prospects drop. Scatter plots reveal relationships, such as spend versus ROAS across campaigns, that a bar chart flattens away.',
      'Part-to-whole is where most dashboards go wrong. A pie or donut chart is readable only with two or three slices; beyond that, a sorted horizontal bar or a stacked bar communicates share far more accurately because the human eye compares lengths better than angles. Distribution questions — "is this average hiding a bimodal split?" — call for a histogram or box plot, not a single summary stat that a noisy metric can quietly distort.',
      'Whatever you choose, the craft is in the defaults: start bar axes at zero, sort ranked bars by value rather than source order, label directly instead of forcing a legend hunt, and pick a color palette that survives color-blindness and grayscale printing. The tools below let you mock up any of these chart types — and the accompanying data table — with realistic styling before you build the real thing, while the glossary entries cover when each one is the right call.',
    ],
    faqs: [
      {
        question: 'How do I choose the right chart type?',
        answer:
          'Start from the question, not the data. Comparing categories → bar chart (horizontal for long labels or rankings, vertical for a few ordered categories). Showing change over time → line or area chart. Part-to-whole → stacked bar or a sorted bar; reserve pie/donut for two or three slices only. Distribution → histogram or box plot. Relationship between two variables → scatter plot. Stage-to-stage drop-off → funnel chart. If two chart types both fit, choose the one with fewer visual elements.',
      },
      {
        question: 'Bar chart vs line chart — which should I use?',
        answer:
          'Use a line chart when the x-axis is continuous (time, most commonly) and you want to show a trend, rate of change, or seasonality. Use a bar chart when the x-axis is categorical and you want to compare discrete values. A quick test: if connecting the points with a line would imply a meaningful in-between value, a line chart is appropriate; if the gaps between categories are meaningless (e.g., campaigns, countries), use bars.',
      },
      {
        question: 'When should I use a pie chart?',
        answer:
          'Rarely, and only for part-to-whole with two or three categories that sum to a meaningful 100%. The human eye compares lengths far more accurately than angles or areas, so once you have four or more slices a sorted horizontal bar chart communicates share more clearly. If you need to show how composition changes over time, a stacked bar or stacked area chart beats a row of pies.',
      },
      {
        question: 'Horizontal vs vertical bar chart?',
        answer:
          'They encode the same information; the choice is about readability. Use horizontal bars when category labels are long, when you have many categories (10+), or when the data is ranked — the eye reads top-to-bottom quickly and labels stay horizontal. Use vertical bars (columns) for a small number of ordered categories or when the categories have a natural left-to-right sequence. For time-series, vertical (or a line chart) matches the convention that time runs left-to-right.',
      },
      {
        question: 'What is the best chart for a marketing conversion funnel?',
        answer:
          'A funnel chart, which shows the count remaining at each stage (impression → click → landing view → add-to-cart → purchase) and makes the largest drop-off visually obvious. If you care more about the conversion rate between specific steps than the absolute volumes, pair the funnel with a small bar chart of step-to-step rates. For flow that splits and recombines across many paths, a Sankey diagram is the better fit.',
      },
      {
        question: 'How do I make charts accessible?',
        answer:
          "Don't rely on color alone — add direct labels, patterns, or text so a color-blind reader (about 8% of men) and a grayscale printout both stay legible. Use a palette with sufficient contrast, keep categorical colors to a handful, start bar axes at zero to avoid exaggerating differences, and give every chart a descriptive title and alt text that states the takeaway, not just the chart type.",
      },
    ],
  },
  {
    id: 'experimentation',
    slug: 'experimentation',
    title: 'Experimentation & Statistics',
    navLabel: 'Experimentation',
    tagline: 'The statistics behind trustworthy tests — significance, sample size, power, and lift.',
    description:
      'Run experiments you can trust. Definitions and calculators for the statistics that decide whether a result is real — statistical significance, sample size, confidence intervals, control groups, and false positives — plus incrementality and A/B-test methodology for marketing teams that refuse to ship on noise.',
    iconName: 'test-tube',
    keywords: [
      'experimentation',
      'a/b testing statistics',
      'statistical significance',
      'sample size calculator',
      'confidence interval',
      'incrementality testing',
      'control group',
    ],
    relatedTopicSlugs: ['creative-testing', 'attribution-measurement', 'data-visualization'],
    content: {
      glossaryTerms: [
        { category: 'creative', slug: 'ab-testing' },
        { category: 'metrics', slug: 'statistical-significance' },
        { category: 'metrics', slug: 'sample-size' },
        { category: 'metrics', slug: 'confidence-interval' },
        { category: 'metrics', slug: 'margin-of-error' },
        { category: 'metrics', slug: 'control-group' },
        { category: 'metrics', slug: 'false-positive' },
        { category: 'metrics', slug: 'false-negative' },
        { category: 'general', slug: 'incrementality' },
      ],
      toolSlugs: [
        'ab-test-statistical-significance-calculator',
        'creative-testing-calculator',
        'ugc-cost-calculator',
        'marketing-incrementality-calculator',
      ],
      templateSlugs: ['ab-test-tracker-template'],
      guideSlugs: ['creative-testing-framework-guide', 'incrementality-testing-guide'],
      blogPostSlugs: [
        'statistical-noise-unmasking-the-illusion-of-insights-in-modern-marketing',
        'how-to-run-an-incrementality-test-that-changes-budget-decisions',
      ],
      blogTopicSlugs: ['data-science'],
    },
    overview: [
      'Most "winning" tests are not. The fastest way to ship false winners is to read results before a test has gathered enough data, to call any difference a result, or to confuse a metric that moved in-platform with revenue that actually grew. Trustworthy experimentation is the discipline of separating signal from noise — and it rests on three dials you set before the test starts, not after.',
      'The first dial is statistical significance, usually expressed as 95% confidence (p < 0.05). It answers a narrow question: if there were truly no difference between A and B, how often would random chance alone produce a gap this large? It does not tell you the variant is better, by how much, or whether the difference matters to the business. The second dial is statistical power, conventionally 80% — the chance your test detects a real effect that exists. Underpowered tests are the silent killer: they quietly miss real wins and leave teams concluding "no difference" when there was one.',
      'The third dial is sample size, and it is driven by your baseline conversion rate and the minimum detectable effect (MDE) you care about. Lower baselines and smaller effects both demand dramatically more traffic. As concrete anchors at 95% confidence and 80% power, a 5% baseline rate needs roughly 31,000 visitors per variant to detect a +10% relative lift, or about 8,100 to detect a +20% lift; a 1% baseline needs roughly 163,000 per variant for a +10% lift. Fix the sample size in advance, then resist peeking — checking repeatedly and stopping the moment you see significance inflates the false-positive rate far above the 5% you think you set.',
      'Finally, a significant in-platform lift is not the same as incremental revenue. Platform attribution credits conversions that often would have happened anyway; the only way to know what your marketing truly caused is a holdout or geo experiment that compares an exposed group to an unexposed control. Use the calculators below to plan sample size and read significance, the glossary to ground the concepts, and incrementality testing to confirm that a statistically real lift is also a real business one.',
    ],
    faqs: [
      {
        question: 'What does statistical significance actually tell me?',
        answer:
          'At 95% confidence (p < 0.05), it tells you that if there were genuinely no difference between your variants, you would see a gap this large by random chance only about 5% of the time. That is all. It does not tell you the variant is better, how large the effect is, or whether it matters commercially — a tiny, business-irrelevant difference can be "statistically significant" with enough traffic. Always pair significance with the effect size and a confidence interval.',
      },
      {
        question: 'How much traffic do I need for an A/B test?',
        answer:
          'It depends on your baseline conversion rate and the minimum detectable effect (MDE). At 95% confidence and 80% power: a 5% baseline needs roughly 31,000 visitors per variant to detect a +10% relative lift, or about 8,100 for a +20% lift; a 1% baseline needs roughly 163,000 per variant for +10%. Lower baselines and smaller target effects both require far more traffic. If the required sample exceeds what you can gather in 2–4 weeks, test a bigger change or a higher-converting funnel step — don\'t compensate by stopping early.',
      },
      {
        question: 'What is statistical power, and why does 80% matter?',
        answer:
          'Power is the probability your test detects a real effect when one truly exists. The 80% convention means you accept a 20% chance of missing a real winner (a false negative). Underpowered tests — too little traffic for the effect you care about — are the most common and least visible testing mistake: they return "no significant difference" on changes that actually worked, so teams stop iterating on ideas that were quietly winning.',
      },
      {
        question: 'Can I stop a test early once it hits significance?',
        answer:
          'No — "peeking" and stopping the moment you see p < 0.05 is the fastest way to ship false positives. Significance fluctuates as data accumulates, so if you check repeatedly and stop at the first significant reading, your true false-positive rate climbs well above 5%. Decide the sample size and run length before launching, and read the result once at the end. If you need to monitor continuously, use a sequential testing method designed for it rather than naive peeking.',
      },
      {
        question: 'What is the difference between statistical significance and incrementality?',
        answer:
          'Significance tells you a measured difference is unlikely to be chance. Incrementality tells you the conversions were actually caused by your marketing rather than ones that would have happened anyway. A campaign can show a significant in-platform lift that is largely non-incremental — retargeting users who were already going to buy. The only way to measure true incrementality is a controlled holdout or geo test comparing an exposed group to an unexposed control; a typical "good" incremental lift for performance campaigns is roughly 10–30%.',
      },
      {
        question: 'What is a minimum detectable effect (MDE)?',
        answer:
          'The MDE is the smallest improvement you want your test to be able to catch — for example, a +10% relative lift on a 5% conversion rate (i.e., moving it to 5.5%, not 15%). It is an input you choose, and it has an enormous effect on required sample size: halving the MDE roughly quadruples the traffic you need. Set it to the smallest change that would actually be worth shipping, not the smallest change imaginable.',
      },
    ],
  },
  {
    id: 'social-proof-creative',
    slug: 'social-proof-creative',
    title: 'Social Proof & Creative Mockups',
    navLabel: 'Creative Mockups',
    tagline: 'Design chat, email, social, review, and ad mockups for testimonials and social proof.',
    description:
      'Free mockup generators — chat, email, social, review, ad, and app-store — to build testimonial slides and social-proof creative without a designer.',
    iconName: 'megaphone',
    keywords: [
      'social proof generator',
      'mockup generator',
      'testimonial screenshot maker',
      'fake chat generator',
      'chat mockup generator',
      'email mockup generator',
      'ad creative mockup',
      'social proof creative',
      'marketing mockup tools',
    ],
    relatedTopicSlugs: ['creative-strategy', 'meta-advertising', 'creative-testing'],
    overview: [
      'Social proof is the cheapest persuasion in marketing — a real customer saying the thing you want a prospect to believe. The hard part is presentation: a raw screenshot is inconsistent, off-brand, or leaks private data, and rebuilding it in a design tool is slow. These generators close that gap by rendering pixel-accurate platform chrome (the exact Slack sidebar, iMessage bubbles, Gmail header, or App Store layout) so you can recreate the moment a deal closed, a feature shipped, or a user raved — and export a retina PNG in a couple of minutes.',
      'The right tool depends on where the proof lives. B2B wins read best as a Slack or email thread; consumer and mobile social proof lands as iMessage, WhatsApp, or a social post; product credibility shows up as reviews and ratings or App Store screenshots. The Email Thread and Fake Chat generators are the workhorses here — pair an inbox screenshot with a chat testimonial on the same slide for a fuller story — while the Review & Testimonial and Social Post generators cover star-rating and feed-native proof.',
      'Fidelity is the point. A mockup that gets the status bar, timestamp, or compose bar subtly wrong undermines the very trust it is meant to build, so each tool encodes the current platform UI rather than an approximation. Everything renders in your browser (nothing is uploaded), so you can use real names and copy, then redact or fictionalize as needed before the asset ships into a deck, ad, landing page, or launch post.',
    ],
    faqs: [
      {
        question: 'What is the best tool for a customer testimonial slide?',
        answer:
          'Match the format to where the testimonial happened. A B2B quote reads most credibly as a Slack message or an email thread (use the Slack/Fake Chat and Email Thread generators); a consumer rave works as an iMessage or WhatsApp screenshot; a star rating belongs in the Review & Testimonial generator. Many launch decks pair an email thread with a chat screenshot on one slide to show the story across inbox and chat.',
      },
      {
        question: 'Are the mockup generators free?',
        answer:
          'Yes — every generator in this hub is free, requires no account, and renders entirely in your browser. You can create unlimited mockups for testimonials, social-proof slides, ad concepts, client decks, or launch creative, and export a retina-density PNG with one click. Nothing you type is uploaded to AdSights.',
      },
      {
        question: 'Can I use these mockups in ads and client decks?',
        answer:
          'They are well-suited to client decks, pitch presentations, landing-page social proof, case studies, and concept work. Be cautious using them in live paid advertising: because the tools recreate real platform UI, ad platforms (Meta, TikTok, and others) restrict creative that misleadingly mimics their interface, and a viewer may mistake a mockup for genuine platform content. For paid placements, use original branded assets or clearly label the mockup. Either way, use real customer quotes with permission, or clearly fictionalize names and content when the example is illustrative.',
      },
      {
        question: 'Why does mockup accuracy matter for social proof?',
        answer:
          'Social proof works only if it looks real. A screenshot with the wrong status-bar icons, an off timestamp, or a missing compose bar reads as fake and undermines the trust it is meant to build. These generators encode the current platform chrome — the exact bubbles, headers, and layouts — so the proof is believable at a glance rather than obviously synthetic.',
      },
    ],
    content: {
      glossaryTerms: [
        { category: 'creative', slug: 'social-proof' },
        { category: 'creative', slug: 'testimonial' },
        { category: 'creative', slug: 'social-proof-ads' },
      ],
      toolSlugs: [
        'email-thread-mockup-generator',
        // GSC-winning landing variants (July 2026): the group-chat, Gmail,
        // and Discord pages rank at striking distance but had zero hub or
        // content inbound links — surfacing them here routes topical equity
        // to the pages Google already prefers for those query clusters.
        'gmail-email-mockup-generator',
        'fake-chat-generator',
        'group-chat-mockup-generator',
        'discord-chat-generator',
        'review-testimonial-generator',
        'social-media-post-mockup-generator',
        // Comment-section mockups (July 2026): the feed-post ↔ comment-thread
        // pairing is the hub's core social-proof workflow, so the newborn
        // umbrella gets its hub inbound link at launch.
        'comment-thread-mockup-generator',
        'ai-chat-mockup-generator',
        'ad-mockup-generator',
        'notification-generator',
        'app-store-screenshot-generator',
        'browser-mockup-generator',
      ],
      templateSlugs: [],
      guideSlugs: ['how-to-spot-a-fake-screenshot'],
      blogPostSlugs: ['free-design-generators-product-hunt-launch', 'social-proof-mockups-email-chat-generators-without-figma'],
    },
  },
] as const

/** Lookup map for O(1) topic resolution by slug. */
export const RESOURCE_TOPIC_BY_SLUG: Readonly<Record<string, ResourceTopicDefinition>> =
  Object.fromEntries(RESOURCE_TOPICS.map((topic) => [topic.slug, topic]))

/** All valid topic slugs for static param generation and validation. */
export const RESOURCE_TOPIC_SLUGS: readonly ResourceTopicId[] = RESOURCE_TOPICS.map(
  (topic) => topic.id,
)

/**
 * Approximate curated resource count from registry refs (excludes blog
 * posts auto-included via blogTopicSlugs).
 *
 * @param {ResourceTopicDefinition} topic - Topic registry entry.
 * @returns {number} Minimum number of explicitly curated resources.
 */
export function getTopicRegistryResourceCount(topic: ResourceTopicDefinition): number {
  const { content } = topic
  return (
    content.glossaryTerms.length +
    content.toolSlugs.length +
    content.templateSlugs.length +
    content.guideSlugs.length +
    content.blogPostSlugs.length +
    (content.comparisonSlugs?.length ?? 0)
  )
}

/**
 * Resolve a topic definition by slug.
 *
 * @param {string} slug - Topic URL slug.
 * @returns {ResourceTopicDefinition | undefined} Matching topic or undefined.
 */
export function getResourceTopicBySlug(slug: string): ResourceTopicDefinition | undefined {
  return RESOURCE_TOPIC_BY_SLUG[slug]
}

/**
 * Topic hubs that feature a given glossary term — the reverse of the registry's
 * `content.glossaryTerms`. Powers the "Featured in topic hubs" backlink on
 * glossary term pages, closing the internal-link loop (hub → term AND term →
 * hub) so authority flows back to the hubs.
 *
 * @param {string} category - Glossary category segment (e.g. `metrics`).
 * @param {string} slug - Glossary term slug.
 * @returns {ResourceTopicDefinition[]} Hubs whose curated terms include this one.
 */
export function getResourceTopicsForGlossaryTerm(
  category: string,
  slug: string,
): ResourceTopicDefinition[] {
  return RESOURCE_TOPICS.filter((topic) =>
    topic.content.glossaryTerms.some((term) => term.category === category && term.slug === slug),
  )
}

/**
 * Lightweight `{ title, slug }` hub links for a glossary term — the serializable
 * shape the `GlossaryTopicHubs` chip needs. Resolve this SERVER-side and pass the
 * result as a prop so the full `RESOURCE_TOPICS` registry (hub overview/FAQ copy
 * included) never enters the glossary client bundle.
 *
 * @param {string} category - Glossary category segment (e.g. `metrics`).
 * @param {string} slug - Glossary term slug.
 * @returns {{ title: string; slug: string }[]} Hub links for the chip row.
 */
export function getTopicHubLinksForGlossaryTerm(
  category: string,
  slug: string,
): { title: string; slug: string }[] {
  return getResourceTopicsForGlossaryTerm(category, slug).map((topic) => ({
    title: topic.title,
    slug: topic.slug,
  }))
}

/**
 * Resource topic hub slugs that auto-include posts from a blog topic slug.
 *
 * @param {string} blogTopicSlug - Blog topic slug (e.g. `creative-strategy`).
 * @returns {string[]} Matching resource topic slugs for ISR revalidation.
 */
export function getResourceTopicSlugsForBlogTopic(blogTopicSlug: string): string[] {
  return RESOURCE_TOPICS.filter((topic) =>
    topic.content.blogTopicSlugs?.includes(blogTopicSlug),
  ).map((topic) => topic.slug)
}

/**
 * Resource topic hub slugs that explicitly list a blog post slug.
 *
 * @param {string} blogPostSlug - Blog post URL slug.
 * @returns {string[]} Matching resource topic slugs for ISR revalidation.
 */
export function getResourceTopicSlugsForBlogPost(blogPostSlug: string): string[] {
  return RESOURCE_TOPICS.filter((topic) =>
    topic.content.blogPostSlugs.includes(blogPostSlug),
  ).map((topic) => topic.slug)
}

/**
 * All resource topic hub paths that should revalidate when a blog post changes.
 *
 * @param {string} blogPostSlug - Blog post URL slug.
 * @param {string} [blogTopicSlug] - Parent blog topic slug when known.
 * @returns {string[]} Unique resource topic slugs to revalidate.
 */
export function getResourceTopicSlugsAffectedByBlogPost(
  blogPostSlug: string,
  blogTopicSlug?: string,
): string[] {
  const slugs = new Set(getResourceTopicSlugsForBlogPost(blogPostSlug))
  if (blogTopicSlug) {
    for (const slug of getResourceTopicSlugsForBlogTopic(blogTopicSlug)) {
      slugs.add(slug)
    }
  }
  return [...slugs]
}
